import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';

const MumbleChannelManager = () => {
  const [channels, setChannels] = useState([]);
  const [flatChannels, setFlatChannels] = useState([]);
  const [groups, setGroups] = useState([]);
  const [mumbleUsers, setMumbleUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showChannelModal, setShowChannelModal] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [channelForm, setChannelForm] = useState({ name: '', parentId: 0 });
  const [groupForm, setGroupForm] = useState({ name: '', channelId: '' });
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupMembers, setGroupMembers] = useState([]);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    fetchData();
    fetchSyncStatus();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [channelsRes, groupsRes, usersRes] = await Promise.all([
        api.get('/mumble-admin/channels'),
        api.get('/mumble-admin/groups'),
        api.get('/mumble-admin/mumble-users')
      ]);

      setChannels(channelsRes.data.channels);
      setFlatChannels(channelsRes.data.flat);
      setGroups(groupsRes.data.groups);
      setMumbleUsers(usersRes.data.users);
    } catch (error) {
      toast.error('Failed to fetch Mumble data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSyncStatus = async () => {
    try {
      const res = await api.get('/mumble-admin/sync-status');
      setSyncStatus(res.data);
    } catch (error) {
      console.error('Failed to fetch sync status:', error);
    }
  };

  const handleSyncUsers = async () => {
    if (!window.confirm('This will import all Mumble server users into the management system. Continue?')) {
      return;
    }

    setSyncing(true);
    try {
      const res = await api.post('/mumble-admin/sync-users');
      toast.success(`Synced ${res.data.synced} users! Skipped ${res.data.skipped} existing users.`);
      fetchSyncStatus();
      // Note: This syncs to management system, not Mumble users list
      // You may want to refresh the full page or user list
    } catch (error) {
      toast.error('Failed to sync users');
      console.error(error);
    } finally {
      setSyncing(false);
    }
  };

  const handleCreateChannel = async (e) => {
    e.preventDefault();
    try {
      await api.post('/mumble-admin/channels', channelForm);
      toast.success('Channel created successfully');
      setShowChannelModal(false);
      setChannelForm({ name: '', parentId: 0 });
      fetchData();
    } catch (error) {
      toast.error('Failed to create channel');
    }
  };

  const handleDeleteChannel = async (channelId) => {
    if (!window.confirm('Are you sure? This will delete all sub-channels and groups.')) {
      return;
    }
    try {
      await api.delete(`/mumble-admin/channels/${channelId}`);
      toast.success('Channel deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete channel');
    }
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    try {
      await api.post('/mumble-admin/groups', groupForm);
      toast.success('Group created successfully');
      setShowGroupModal(false);
      setGroupForm({ name: '', channelId: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create group');
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('Are you sure you want to delete this group?')) {
      return;
    }
    try {
      await api.delete(`/mumble-admin/groups/${groupId}`);
      toast.success('Group deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete group');
    }
  };

  const handleSelectGroup = async (group) => {
    setSelectedGroup(group);
    try {
      const res = await api.get(`/mumble-admin/groups/${group.group_id}/members`);
      setGroupMembers(res.data.members);
    } catch (error) {
      toast.error('Failed to fetch group members');
    }
  };

  const handleAddUserToGroup = async (userId) => {
    try {
      await api.post(`/mumble-admin/groups/${selectedGroup.group_id}/members`, { userId });
      toast.success('User added to group successfully');
      handleSelectGroup(selectedGroup); // Refresh members
    } catch (error) {
      toast.error('Failed to add user to group');
    }
  };

  const handleRemoveUserFromGroup = async (userId) => {
    try {
      await api.delete(`/mumble-admin/groups/${selectedGroup.group_id}/members/${userId}`);
      toast.success('User removed from group successfully');
      handleSelectGroup(selectedGroup); // Refresh members
    } catch (error) {
      toast.error('Failed to remove user from group');
    }
  };

  const handleSetDefaultChannel = async (userId, channelId) => {
    try {
      await api.put(`/mumble-admin/mumble-users/${userId}/default-channel`, { channelId });
      toast.success('Default channel set successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to set default channel');
    }
  };

  const renderChannelTree = (channelList, level = 0) => {
    return channelList.map((channel) => (
      <div key={channel.channel_id} style={{ marginLeft: `${level * 20}px` }}>
        <div className="channel-item">
          <span className="channel-name">
            {channel.channel_id === 0 ? '🏠' : '📁'} {channel.name}
          </span>
          {channel.channel_id !== 0 && (
            <button
              onClick={() => handleDeleteChannel(channel.channel_id)}
              className="btn btn-sm btn-danger"
            >
              Delete
            </button>
          )}
        </div>
        {channel.children && channel.children.length > 0 && renderChannelTree(channel.children, level + 1)}
      </div>
    ));
  };

  if (loading) {
    return <div className="loading">Loading Mumble management...</div>;
  }

  return (
    <div className="mumble-manager">
      {/* Sync Status Banner */}
      {syncStatus && syncStatus.unsyncedCount > 0 && (
        <div className="sync-banner">
          <div className="sync-info">
            <strong>⚠️ Unsynced Users Detected</strong>
            <p>
              {syncStatus.unsyncedCount} user(s) exist in Mumble server but not in the management system.
              <br />
              Mumble users: {syncStatus.mumbleUserCount} | Management users: {syncStatus.managementUserCount}
            </p>
            {syncStatus.unsyncedUsers && syncStatus.unsyncedUsers.length > 0 && (
              <details style={{ marginTop: '10px' }}>
                <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>
                  View unsynced users
                </summary>
                <ul style={{ marginTop: '5px' }}>
                  {syncStatus.unsyncedUsers.map((u) => (
                    <li key={u.userId}>
                      {u.name} {u.email && `(${u.email})`}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
          <button
            onClick={handleSyncUsers}
            disabled={syncing}
            className="btn btn-warning"
          >
            {syncing ? 'Syncing...' : 'Sync Users Now'}
          </button>
        </div>
      )}

      <div className="row">
        {/* Channels Section */}
        <div className="col-md-6">
          <div className="card">
            <div className="card-header">
              <h3>Channels</h3>
              <button
                onClick={() => setShowChannelModal(true)}
                className="btn btn-primary"
              >
                + Create Channel
              </button>
            </div>
            <div className="card-body">
              {renderChannelTree(channels)}
            </div>
          </div>
        </div>

        {/* Groups Section */}
        <div className="col-md-6">
          <div className="card">
            <div className="card-header">
              <h3>Groups</h3>
              <button
                onClick={() => setShowGroupModal(true)}
                className="btn btn-primary"
              >
                + Create Group
              </button>
            </div>
            <div className="card-body">
              <div className="groups-list">
                {groups.map((group) => (
                  <div key={group.group_id} className="group-item">
                    <div>
                      <strong>{group.name}</strong>
                      <br />
                      <small>Channel: {group.channel_name || 'Root'}</small>
                    </div>
                    <div>
                      <button
                        onClick={() => handleSelectGroup(group)}
                        className="btn btn-sm btn-info"
                      >
                        Manage Members
                      </button>
                      <button
                        onClick={() => handleDeleteGroup(group.group_id)}
                        className="btn btn-sm btn-danger"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mumble Users Section */}
      <div className="row mt-4">
        <div className="col-md-12">
          <div className="card">
            <div className="card-header">
              <h3>Mumble Users & Default Channels</h3>
            </div>
            <div className="card-body">
              <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Email</th>
                    <th>Current Default Channel</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {mumbleUsers.map((user) => (
                    <tr key={user.user_id}>
                      <td>{user.name}</td>
                      <td>{user.email || 'N/A'}</td>
                      <td>
                        {flatChannels.find(c => c.channel_id === user.lastchannel)?.name || 'Root'}
                      </td>
                      <td>
                        <select
                          className="form-select form-select-sm"
                          value={user.lastchannel || 0}
                          onChange={(e) => handleSetDefaultChannel(user.user_id, parseInt(e.target.value))}
                        >
                          {flatChannels.map((channel) => (
                            <option key={channel.channel_id} value={channel.channel_id}>
                              {channel.name}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Channel Modal */}
      {showChannelModal && (
        <div className="modal-overlay" onClick={() => setShowChannelModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Create New Channel</h3>
            <form onSubmit={handleCreateChannel}>
              <div className="form-group">
                <label>Channel Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={channelForm.name}
                  onChange={(e) => setChannelForm({ ...channelForm, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Parent Channel</label>
                <select
                  className="form-control"
                  value={channelForm.parentId}
                  onChange={(e) => setChannelForm({ ...channelForm, parentId: parseInt(e.target.value) })}
                >
                  {flatChannels.map((channel) => (
                    <option key={channel.channel_id} value={channel.channel_id}>
                      {channel.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="modal-actions">
                <button type="submit" className="btn btn-primary">Create</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowChannelModal(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Group Modal */}
      {showGroupModal && (
        <div className="modal-overlay" onClick={() => setShowGroupModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Create New Group</h3>
            <form onSubmit={handleCreateGroup}>
              <div className="form-group">
                <label>Group Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={groupForm.name}
                  onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Channel</label>
                <select
                  className="form-control"
                  value={groupForm.channelId}
                  onChange={(e) => setGroupForm({ ...groupForm, channelId: parseInt(e.target.value) })}
                  required
                >
                  <option value="">Select a channel</option>
                  {flatChannels.map((channel) => (
                    <option key={channel.channel_id} value={channel.channel_id}>
                      {channel.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="modal-actions">
                <button type="submit" className="btn btn-primary">Create</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowGroupModal(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Group Members Modal */}
      {selectedGroup && (
        <div className="modal-overlay" onClick={() => setSelectedGroup(null)}>
          <div className="modal-content modal-lg" onClick={(e) => e.stopPropagation()}>
            <h3>Manage Group: {selectedGroup.name}</h3>

            <div className="row">
              <div className="col-md-6">
                <h4>Current Members</h4>
                <ul className="list-group">
                  {groupMembers.map((member) => (
                    <li key={member.user_id} className="list-group-item d-flex justify-content-between">
                      <span>{member.username}</span>
                      <button
                        onClick={() => handleRemoveUserFromGroup(member.user_id)}
                        className="btn btn-sm btn-danger"
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                  {groupMembers.length === 0 && (
                    <li className="list-group-item text-muted">No members yet</li>
                  )}
                </ul>
              </div>

              <div className="col-md-6">
                <h4>Available Users</h4>
                <ul className="list-group">
                  {mumbleUsers
                    .filter(user => !groupMembers.find(m => m.user_id === user.user_id))
                    .map((user) => (
                      <li key={user.user_id} className="list-group-item d-flex justify-content-between">
                        <span>{user.name}</span>
                        <button
                          onClick={() => handleAddUserToGroup(user.user_id)}
                          className="btn btn-sm btn-success"
                        >
                          Add
                        </button>
                      </li>
                    ))}
                </ul>
              </div>
            </div>

            <div className="modal-actions mt-3">
              <button className="btn btn-secondary" onClick={() => setSelectedGroup(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .mumble-manager {
          padding: 20px;
        }
        .sync-banner {
          background-color: #fff3cd;
          border: 2px solid #ffc107;
          border-radius: 8px;
          padding: 15px;
          margin-bottom: 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 15px;
        }
        .sync-info {
          flex: 1;
        }
        .sync-info p {
          margin: 5px 0;
          color: #856404;
        }
        .sync-banner .btn {
          white-space: nowrap;
        }
        .card {
          margin-bottom: 20px;
          border: 1px solid #ddd;
          border-radius: 8px;
        }
        .card-header {
          padding: 15px;
          background-color: #f8f9fa;
          border-bottom: 1px solid #ddd;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .card-body {
          padding: 15px;
        }
        .channel-item {
          padding: 8px;
          margin: 4px 0;
          border-bottom: 1px solid #eee;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .channel-name {
          font-weight: 500;
        }
        .group-item {
          padding: 12px;
          margin: 8px 0;
          border: 1px solid #ddd;
          border-radius: 4px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .group-item button {
          margin-left: 8px;
        }
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }
        .modal-content {
          background-color: white;
          padding: 30px;
          border-radius: 8px;
          width: min(500px, 90vw);
          max-height: 90vh;
          overflow-y: auto;
        }
        .modal-lg {
          width: min(800px, 90vw);
        }
        @media (max-width: 600px) {
          .modal-content {
            padding: 20px;
          }
        }
        .modal-actions {
          display: flex;
          gap: 10px;
          margin-top: 20px;
          justify-content: flex-end;
        }
        .form-group {
          margin-bottom: 15px;
        }
        .form-group label {
          display: block;
          margin-bottom: 5px;
          font-weight: 500;
        }
        .form-control, .form-select {
          width: 100%;
          padding: 8px;
          border: 1px solid #ddd;
          border-radius: 4px;
        }
        .btn {
          padding: 8px 16px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-weight: 500;
        }
        .btn-primary {
          background-color: #007bff;
          color: white;
        }
        .btn-secondary {
          background-color: #6c757d;
          color: white;
        }
        .btn-danger {
          background-color: #dc3545;
          color: white;
        }
        .btn-success {
          background-color: #28a745;
          color: white;
        }
        .btn-info {
          background-color: #17a2b8;
          color: white;
        }
        .btn-sm {
          padding: 4px 8px;
          font-size: 0.875rem;
        }
        .list-group {
          list-style: none;
          padding: 0;
        }
        .list-group-item {
          padding: 10px 15px;
          border: 1px solid #ddd;
          margin-bottom: 4px;
        }
        .table {
          width: 100%;
          border-collapse: collapse;
        }
        .table th,
        .table td {
          padding: 12px;
          text-align: left;
          border-bottom: 1px solid #ddd;
        }
        .table th {
          background-color: #f8f9fa;
          font-weight: 600;
        }
      `}</style>
    </div>
  );
};

export default MumbleChannelManager;
